import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Linkedin, ArrowUpRight, MapPin } from 'lucide-react';
import whatsappIcon from '../../assets/WhatsappICON.png';
import logoWhite from '../../assets/WE24-Logo - BEYAZ-Crop.png';
import { useLanguage } from '../../context/LanguageContext';
import { tr } from '../../locales/tr';
import { en } from '../../locales/en';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.footer : en.footer;

  const servicesLinks = language === 'TR' ? [
    { name: "Film & Video Prodüksiyon", path: "/hizmetler/video-produksiyon" },
    { name: "Canlı Yayın & Reji", path: "/hizmetler/canli-yayin-program" },
    { name: "Post-Prodüksiyon & VFX", path: "/hizmetler/post-produksiyon" },
    { name: "Fotoğraf Prodüksiyonu", path: "/hizmetler/fotograf-produksiyon" },
    { name: "Dijital & Sosyal Medya", path: "/hizmetler/dijital-ve-sosyal-medya" },
    { name: "Tasarım ve Marka", path: "/hizmetler/tasarim-ve-marka" },
    { name: "Web ve Yazılım", path: "/hizmetler/web-ve-yazilim" },
  ] : [
    { name: "Film & Video Production", path: "/en/services/video-produksiyon" },
    { name: "Live Broadcasting", path: "/en/services/canli-yayin-program" },
    { name: "Post-Production & VFX", path: "/en/services/post-produksiyon" },
    { name: "Photography Production", path: "/en/services/fotograf-produksiyon" },
    { name: "Digital & Social Media", path: "/en/services/dijital-ve-sosyal-medya" },
    { name: "Brand & Design", path: "/en/services/tasarim-ve-marka" },
    { name: "Web & Software", path: "/en/services/web-ve-yazilim" },
  ];

  const quickLinks = language === 'TR' ? [
    { name: "Projelerimiz", path: "/projeler" },
    { name: "Hizmetlerimiz", path: "/hizmetler" },
    { name: "İçgörüler & Rehberler", path: "/icgoruler" },
    { name: "Hakkımızda", path: "/hakkimizda" },
    { name: "Markalar", path: "/markalar" },
    { name: "İletişim", path: "/iletisim" },
  ] : [
    { name: "Our Projects", path: "/en/projects" },
    { name: "Our Services", path: "/en/services" },
    { name: "Insights & Playbook", path: "/en/insights" },
    { name: "About Us", path: "/en/about" },
    { name: "Brands", path: "/en/brands" },
    { name: "Contact", path: "/en/contact" },
  ];

  const socialLinks = [
    { icon: <Instagram className="w-4 h-4" />, url: "https://www.instagram.com/we24_agency/", label: "Instagram" },
    { icon: <Facebook className="w-4 h-4" />, url: "https://www.facebook.com/we24agency/", label: "Facebook" },
    { icon: <Youtube className="w-4 h-4" />, url: "https://www.youtube.com/@WE24agency", label: "YouTube" },
    { icon: <Linkedin className="w-4 h-4" />, url: "https://www.linkedin.com/company/67730275/", label: "LinkedIn" },
    { icon: <MapPin className="w-4 h-4 text-[#ff7a63]" />, url: "https://maps.google.com/?q=Me%C5%9Frutiyet+Mahallesi+%C5%9Eehit+%C4%B0smet+Arma%C4%9Fan+Sokak+No+7+%C5%9Ei%C5%9Fli+%C4%B0stanbul", label: "Google Haritalar" },
  ];

  return (
    <footer className="bg-card/30 border-t border-border/30 pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
        {/* Info Column */}
        <div className="flex flex-col gap-6">
          <Link to={language === 'TR' ? "/" : "/en"} className="flex items-center group py-1" aria-label="WE24 Agency Ana Sayfa">
            <img
              src={logoWhite}
              alt="WE24 Agency"
              className="h-8 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </Link>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t.description}
          </p>
          <div className="flex items-center gap-3">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-border/20 border border-border/30 flex items-center justify-center text-muted-foreground hover:text-white hover:bg-border/40 hover:scale-105 transition-all"
                aria-label={social.label}
              >
                {social.icon}
              </a>
            ))}
          </div>
        </div>

        {/* Services Column */}
        <div className="flex flex-col gap-6">
          <p className="text-sm font-semibold tracking-wider text-foreground uppercase">{t.servicesTitle}</p>
          <ul className="flex flex-col gap-3">
            {servicesLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className="text-sm text-muted-foreground hover:text-white transition-colors"
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Navigation Column */}
        <div className="flex flex-col gap-6">
          <p className="text-sm font-semibold tracking-wider text-foreground uppercase">{t.quickLinksTitle}</p>
          <ul className="flex flex-col gap-3">
            {quickLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className="text-sm text-muted-foreground hover:text-white transition-colors"
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Column */}
        <div className="flex flex-col gap-6">
          <p className="text-sm font-semibold tracking-wider text-foreground uppercase">{t.contactTitle}</p>
          <div className="flex flex-col gap-3 text-sm text-muted-foreground">
            <p>{t.address}</p>
            <a href="tel:+905452292424" className="hover:text-white transition-colors">
              T: +90 545 229 24 24
            </a>
            <a href="mailto:info@we24agency.com" className="hover:text-white transition-colors">
              E: info@we24agency.com
            </a>
          </div>
          <div>
            <a
              href="https://wa.me/905452292424"
              target="_blank"
              rel="noopener noreferrer"
              className="liquid-glass text-xs uppercase font-medium tracking-wider px-5 py-2.5 rounded-full inline-flex items-center gap-2 border border-white/10 hover:border-emerald-500/40 text-foreground group transition-colors"
            >
              <img src={whatsappIcon} alt="WhatsApp" className="w-4 h-4 object-contain group-hover:scale-110 transition-transform" />
              <span>{t.whatsappBtn}</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" />
            </a>
          </div>
        </div>
      </div>

      {/* Legal Subfooter */}
      <div className="max-w-7xl mx-auto px-6 border-t border-border/20 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
        <div>
          © {currentYear} WE24 Agency. {t.allRightsReserved}
        </div>
        <div className="flex items-center gap-6">
          <Link to={language === 'TR' ? "/gizlilik" : "/en/privacy"} className="hover:text-white transition-colors">
            {t.privacy}
          </Link>
          <Link to={language === 'TR' ? "/kvkk" : "/en/kvkk"} className="hover:text-white transition-colors">
            {t.kvkk}
          </Link>
          <Link to={language === 'TR' ? "/cerez-politikasi" : "/en/cookie-policy"} className="hover:text-white transition-colors">
            {t.cookie}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
